import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  isMobileBreakpoint,
  clamp,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** Index du media actuellement affiche, pour les fleches et le compteur. */
  #activeIndex = 0;

  /** Media actuellement grossi sur desktop, s'il y en a un. */
  #zoomedContainer = /** @type {HTMLElement | null} */ (null);

  /**
   * Rectangle de l'image AVANT grossissement. Il sert a convertir la position
   * de la souris en origine de transformation : une fois l'image agrandie, son
   * getBoundingClientRect ne decrit plus la zone d'origine.
   */
  #zoomRect = /** @type {DOMRect | null} */ (null);

  /**
   * Derniere position de souris pas encore appliquee. Un trackpad emet bien
   * plus d'evenements que l'ecran n'affiche d'images : on ne retient que la
   * derniere et on ecrit l'origine une seule fois par frame.
   */
  #pendingPointer = /** @type {{ x: number, y: number } | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.resetZoom);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.resetZoom);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.setActiveIndex(index);

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    // On change de visuel : le grossissement en cours n'a plus lieu d'etre.
    if (activeIndex !== this.#activeIndex) this.resetZoom();
    this.setActiveIndex(activeIndex);

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    this.resetZoom();

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      return this.previous();
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      return this.next();
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();

    // Premiere pression : on degrossit. Seconde : on ferme.
    if (this.#zoomedContainer) return this.resetZoom();

    this.close();
  }

  /* ==========================================================================
     Navigation et grossissement desktop (Max)
     ========================================================================== */

  /**
   * Met a jour l'index courant, le compteur et l'etat des fleches.
   * @param {number | string} index - L'index du media affiche.
   */
  setActiveIndex(index) {
    const { media, counter, previousButton, nextButton } = this.refs;
    const activeIndex = Number(index);

    if (Number.isNaN(activeIndex)) return;

    this.#activeIndex = activeIndex;

    if (counter instanceof HTMLElement) {
      counter.textContent = `${activeIndex + 1} / ${media.length}`;
    }

    if (previousButton instanceof HTMLButtonElement) {
      previousButton.disabled = activeIndex <= 0;
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = activeIndex >= media.length - 1;
    }
  }

  /** Affiche le visuel suivant. */
  next() {
    this.selectThumbnail(this.#activeIndex + 1);
  }

  /** Affiche le visuel precedent. */
  previous() {
    this.selectThumbnail(this.#activeIndex - 1);
  }

  /**
   * Clic sur un media de la lightbox.
   *
   * Sur l'image elle-meme : on grossit, ou on degrossit si c'etait deja le cas.
   * A cote de l'image : on ferme, comme le faisait Horizon.
   * En mobile : le clic ferme, le zoom tactile restant gere par
   * drag-zoom-wrapper.
   *
   * @param {number | string} index - L'index du media clique.
   * @param {PointerEvent} event - L'evenement de clic.
   */
  handleMediaClick(index, event) {
    if (isMobileBreakpoint()) return this.close();

    // `event.target` est reecrit par le systeme de composants vers l'element qui
    // porte l'attribut `on:click`, ici le <li> plein ecran. Le chemin de
    // propagation, lui, donne bien l'element reellement clique.
    const target = event.composedPath?.()[0] ?? event.target;
    const image = target instanceof Element ? target.closest('.product-media__image') : null;

    if (!image) return this.close();

    // L'image est posee sur toute la case disponible et se contient dedans :
    // son cadre deborde donc la photo visible. Un clic dans cette marge doit
    // fermer, pas grossir.
    const picture = getPictureRect(image);
    const insidePicture =
      event.clientX >= picture.left &&
      event.clientX <= picture.right &&
      event.clientY >= picture.top &&
      event.clientY <= picture.bottom;

    if (!insidePicture) return this.close();

    this.toggleZoom(index, event);
  }

  /**
   * Grossit le media vise, ou revient a la taille normale s'il l'etait deja.
   * @param {number | string} index - L'index du media.
   * @param {PointerEvent} event - L'evenement de clic, pour l'origine du zoom.
   */
  toggleZoom(index, event) {
    const container = this.refs.media[Number(index)];

    if (!container) return;
    if (this.#zoomedContainer === container) return this.resetZoom();

    this.resetZoom();

    const image = container.querySelector('.product-media__image');

    if (!image) return;

    // Mesure avant grossissement : c'est ce rectangle qui sert de repere.
    this.#zoomRect = image.getBoundingClientRect();
    this.#zoomedContainer = container;

    container.classList.add('product-media-container--zoomed');
    container.addEventListener('pointermove', this.handleZoomPointerMove);

    this.handleZoomPointerMove(event);
  }

  /** Revient a la taille normale et remet le media dans son etat de depart. */
  resetZoom = () => {
    const container = this.#zoomedContainer;

    if (!container) return;

    container.classList.remove('product-media-container--zoomed');
    container.removeEventListener('pointermove', this.handleZoomPointerMove);
    container.style.removeProperty('--ef-zoom-x');
    container.style.removeProperty('--ef-zoom-y');

    this.#zoomedContainer = null;
    this.#zoomRect = null;
  };

  /**
   * Deplace l'origine du grossissement pour qu'elle suive la souris.
   * @param {PointerEvent | MouseEvent} event - Le mouvement de souris.
   */
  handleZoomPointerMove = (event) => {
    // Une frame est deja programmee : on remplace juste la position retenue.
    const frameScheduled = this.#pendingPointer !== null;

    this.#pendingPointer = { x: event.clientX, y: event.clientY };

    // Les callbacks de requestAnimationFrame passent avant le rendu : meme au
    // premier clic, l'origine est posee avant que le grossissement s'affiche.
    if (!frameScheduled) requestAnimationFrame(this.#applyZoomOrigin);
  };

  /** Ecrit l'origine du grossissement a partir de la derniere position retenue. */
  #applyZoomOrigin = () => {
    const pointer = this.#pendingPointer;
    const container = this.#zoomedContainer;
    const rect = this.#zoomRect;

    this.#pendingPointer = null;

    // Le grossissement a pu etre annule entre l'evenement et la frame.
    if (!pointer || !container || !rect || !rect.width || !rect.height) return;

    const x = clamp(((pointer.x - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((pointer.y - rect.top) / rect.height) * 100, 0, 100);

    container.style.setProperty('--ef-zoom-x', `${x}%`);
    container.style.setProperty('--ef-zoom-y', `${y}%`);
  };

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.resetZoom();
    this.setActiveIndex(index);

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Rectangle reellement occupe par la photo dans un element image affiche en
 * `object-fit: contain` : le cadre de l'element est plus grand des qu'un des
 * deux formats ne correspond pas.
 *
 * @param {Element} image - L'element image.
 * @returns {DOMRect} Le rectangle de la photo visible.
 */
function getPictureRect(image) {
  const rect = image.getBoundingClientRect();

  if (!(image instanceof HTMLImageElement)) return rect;

  const { naturalWidth, naturalHeight } = image;

  if (!naturalWidth || !naturalHeight) return rect;

  const scale = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;

  return new DOMRect(rect.left + (rect.width - width) / 2, rect.top + (rect.height - height) / 2, width, height);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
